// src/Authentication/Register.js
import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle, Loader, User } from 'lucide-react';
import authService from './AuthService';
import arcLogo from '../images/arc-logo.png';

const Register = ({ onRegister, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (message) setMessage('');
  };

  const validateForm = () => {
    if (!formData.displayName.trim()) { setError('Please enter your full name.'); return false; }
    if (!formData.email.trim()) { setError('Please enter your email address.'); return false; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters long.'); return false; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match.'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!validateForm()) return;
    setLoading(true);
    try {
      const result = await authService.register(
        formData.email,
        formData.password,
        { displayName: formData.displayName.trim() }
      );
      if (result.success) {
        setMessage(result.message);
        if (onRegister) onRegister(result.user);
        setFormData({ displayName: '', email: '', password: '', confirmPassword: '' });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return { strength: 0, text: '', color: 'bg-arc-line' };
    if (password.length < 6) return { strength: 25, text: 'Too short', color: 'bg-red-500' };
    if (password.length < 8) return { strength: 50, text: 'Weak', color: 'bg-amber-500' };
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return { strength: 75, text: 'Good', color: 'bg-arc-deep' };
    }
    return { strength: 100, text: 'Strong', color: 'bg-arc-gold' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="min-h-screen mesh flex items-center justify-center p-4">
      <div className="surface-xl max-w-md w-full p-8 sm:p-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <img
              src={arcLogo}
              alt="ARCrypto"
              className="h-16 sm:h-20 w-auto object-contain"
            />
          </div>
          <p className="eyebrow mb-3 text-arc-gold">Be DEFIant</p>
          <h1 className="headline text-3xl mb-2">
            Create your <span className="text-arc-gold">FIRE</span>Tracker
          </h1>
          <p className="text-arc-muted text-sm">Start tracking your path to financial sovereignty.</p>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="mb-5 p-3 rounded-xl border border-arc-deep/40 bg-arc-deep/10 flex items-start gap-2.5">
            <AlertCircle className="text-arc-light flex-shrink-0 mt-0.5" size={18} />
            <span className="text-arc-light text-sm leading-relaxed">{error}</span>
          </div>
        )}

        {message && (
          <div className="mb-5 p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 flex items-start gap-2.5">
            <CheckCircle className="text-emerald-400 flex-shrink-0 mt-0.5" size={18} />
            <span className="text-emerald-300 text-sm leading-relaxed">{message}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Full Name */}
          <div>
            <label htmlFor="displayName" className="eyebrow block mb-2">Full name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Your full name"
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="eyebrow block mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@arcrypto.io"
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="eyebrow block mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                className="w-full pl-10 pr-12 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-arc-dim hover:text-arc-ink transition-colors"
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {formData.password && (
              <div className="mt-2.5">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim">Strength</span>
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-muted">{passwordStrength.text}</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-1">
                  <div
                    className={`h-1 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                    style={{ width: `${passwordStrength.strength}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="eyebrow block mb-2">Confirm password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="w-full pl-10 pr-12 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-arc-dim hover:text-arc-ink transition-colors"
                disabled={loading}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {formData.confirmPassword && (
              <div className="mt-2 flex items-center gap-1.5">
                {formData.password === formData.confirmPassword ? (
                  <>
                    <CheckCircle size={14} className="text-emerald-400" />
                    <span className="text-xs text-emerald-300">Passwords match</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={14} className="text-arc-light" />
                    <span className="text-xs text-arc-light">Passwords do not match</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="btn-tactile btn-gold w-full py-3 px-4 rounded-xl text-sm font-semibold flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader className="animate-spin mr-2" size={18} />
                Creating account…
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="my-7 rule" />

        {/* Login Link */}
        <div className="text-center">
          <p className="text-sm text-arc-muted">
            Already have an account?{' '}
            <button
              onClick={onSwitchToLogin}
              className="text-arc-gold hover:text-arc-light font-medium transition-colors"
              disabled={loading}
            >
              Sign in
            </button>
          </p>
        </div>

        {/* Terms */}
        <p className="mt-5 text-center text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim">
          By creating an account, you agree to our{' '}
          <a href="#" className="text-arc-deep hover:text-arc-gold transition-colors">Terms</a>
          {' '}and{' '}
          <a href="#" className="text-arc-deep hover:text-arc-gold transition-colors">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default Register;
